import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildingMemberRefusal, can, canAt, facilityAdminRefusal } from "@alm/policy";
import { MyFaceCard } from "@/components/app/faces/my-face-card";
import { StaffFaceCard } from "@/components/app/faces/staff-face-card";
import { LicenseCard } from "@/components/app/workforce/license-card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { orpc } from "@/lib/orpc";
import { AccountForm } from "./-components/account-form";
import { DeleteStaffButton } from "./-components/delete-staff-button";
import { EmploymentForm } from "./-components/employment-form";
import { FacilityAccess } from "./-components/facility-access";
import { HandoverDialog } from "./-components/handover-dialog";
import { HiringHistoryCard } from "./-components/hiring-history-card";
import { ImpersonateButton } from "./-components/impersonate-button";
import { PayCard } from "./-components/pay-card";
import { SignOutEverywhereButton } from "./-components/sign-out-everywhere-button";
import { StaffHeader, type StaffTab } from "./-components/staff-header";
import { EmployeeHrFile } from "@/routes/_authenticated/_workforce/hr/-components/employee-hr-file";
import {
  useCanAnywhere,
  useCanOrganizationWide,
  useCanWithoutBuilding,
  useFeature,
  usePolicy,
} from "@/lib/policy";

export const Route = createFileRoute("/_authenticated/_administration/employees/$userId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.administration.staff.get.queryOptions({ input: { userId: params.userId } }),
    ),
  component: StaffDetailPage,
});

/** Tabs decide which halves exist: a volunteer or vendor has an account and access, but no HR or
 * hiring file. */
function StaffDetailPage() {
  const { userId } = Route.useParams();
  const [tab, setTab] = useState<StaffTab>("account");
  const [handingOver, setHandingOver] = useState(false);
  const { data: person } = useSuspenseQuery(
    orpc.administration.staff.get.queryOptions({ input: { userId } }),
  );
  const { subject } = usePolicy();
  /* A person is not a building: these doors name none, so the building on the switcher does
     not answer them. A building's administrator is answered person by person (`managed`). */
  const canManageRoles = useCanWithoutBuilding("organization:manage-members");
  const canDelete = useCanOrganizationWide("organization:manage-members");
  const canImpersonate = useCanWithoutBuilding("staff:impersonate") && subject.platformAdmin;
  // Held only at some buildings, the right reaches plain staff who work solely there, as on the
  // server.
  const deactivatesAnybody = useCanOrganizationWide("staff:deactivate");
  const deactivatesSomewhere = useCanAnywhere("staff:deactivate");
  const canDeactivate =
    deactivatesAnybody ||
    (deactivatesSomewhere && facilityAdminRefusal(subject, person, "staff:deactivate") === null);
  // A building admin edits the account of somebody who works only at their
  // buildings (the server's answer, on the record), and changes anybody's
  // place at a building they run; roles and deletion stay the organization's.
  const managed = person.facilityAdminManages;
  const canEditProfile = useCanWithoutBuilding("staff:update") || managed;
  // A role that manages a building's staff (HR) works through the building
  // rule below; only the organization's own right skips it, as on the server.
  const orgAssigns = canManageRoles;
  const assignsSomewhere = canAt(subject, "facility:manage-members", null);
  const canAssignAt = (facilityId: string) =>
    orgAssigns || buildingMemberRefusal(subject, person, facilityId) === null;
  const canReadHr = useCanWithoutBuilding("hr:read");
  const hrOn = useFeature("hr", null);
  const hiringOn = useFeature("hiring", null);
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.staff.key() });
  const photo = useQuery(orpc.administration.staff.photo.url.queryOptions({ input: { userId } }));
  const setActive = useMutation(
    orpc.administration.staff.setActive.mutationOptions({
      // Reversible, so an Undo rather than a confirmation that slows every deliberate press.
      onSuccess: async (_result, variables) => {
        toast.success(`${person.name} ${variables.isActive ? "reactivated" : "deactivated"}`, {
          action: {
            label: "Undo",
            onClick: () => setActive.mutate({ userId, isActive: !variables.isActive }),
          },
        });
        await invalidate();
      },
      onError: (e) => toast.error(errorWords(e)),
    }),
  );
  const isSelf = person.id === subject.userId;
  const isOwner = person.orgRole === "owner";
  const showHr = canReadHr && hrOn && person.isEmployee;
  /* Pay is HR's: who manages HR where they work. The server asks the same. */
  const showPay =
    person.isEmployee &&
    (can(subject, "hr:manage") ||
      person.facilities.some((row) => can(subject, "hr:manage", { facilityId: row.facilityId })));

  const TAB = "flex-none px-2.5";
  const tabsList = (
    /* `fit="wrap"`: six tabs do not fit a phone, and a scrolling bar cut the
       last ones off with nothing to say they were there. Along the header
       card's bottom edge, as on a resident's chart. */
    <TabsList fit="wrap" variant="line" className="justify-start">
      <TabsTrigger value="account" className={TAB}>
        Account
      </TabsTrigger>
      {person.isEmployee ? (
        <TabsTrigger value="employment" className={TAB}>
          Employment
        </TabsTrigger>
      ) : null}
      <TabsTrigger value="role" className={TAB}>
        Roles &amp; access
      </TabsTrigger>
      {person.isEmployee ? (
        <TabsTrigger value="licenses" className={TAB}>
          Licenses
        </TabsTrigger>
      ) : null}
      {showHr ? (
        <TabsTrigger value="hr-docs" className={TAB}>
          HR forms and documents
        </TabsTrigger>
      ) : null}
      {person.isEmployee && hiringOn ? (
        <TabsTrigger value="hiring" className={TAB}>
          Hiring
        </TabsTrigger>
      ) : null}
    </TabsList>
  );

  return (
    <Tabs value={tab} onValueChange={(value) => setTab(value as StaffTab)}>
      <StaffHeader
        person={person}
        photoUrl={photo.data?.url}
        showHr={showHr}
        onOpenTab={setTab}
        tabs={tabsList}
        actions={
          isSelf ? null : (
            <>
              {/* Editing lives in the tabs; what is left here is what leaves the record. */}
              {canDeactivate ? (
                <Button
                  variant="outline"
                  onClick={() => setActive.mutate({ userId, isActive: !person.isActive })}
                >
                  {person.isActive ? "Deactivate" : "Reactivate"}
                </Button>
              ) : null}
              {canDeactivate && person.isActive ? (
                <SignOutEverywhereButton userId={userId} name={person.name} />
              ) : null}
              {/* Somebody leaving: who takes over their calls, number and leads, then off. */}
              {canDeactivate ? (
                <Button variant="outline" onClick={() => setHandingOver(true)}>
                  {person.isActive ? "Deactivate and hand over" : "Hand over work"}
                </Button>
              ) : null}
              {canImpersonate && person.isActive ? (
                <ImpersonateButton userId={userId} name={person.name} />
              ) : null}
              {canDelete ? <DeleteStaffButton userId={userId} name={person.name} danger /> : null}
            </>
          )
        }
      />

      {handingOver ? (
        <HandoverDialog
          userId={userId}
          name={person.name ?? "them"}
          isActive={person.isActive ?? false}
          open={handingOver}
          onOpenChange={setHandingOver}
        />
      ) : null}

      <TabsContent value="account" className="mt-4 flex flex-col gap-4">
        <AccountForm
          person={person}
          canManage={canEditProfile}
          canManageLogin={(canManageRoles || managed) && !isSelf}
          canDeactivate={canDeactivate && !isSelf}
        />
        {isSelf ? (
          <MyFaceCard hasPhoto={Boolean(person.profilePhotoPath)} />
        ) : (
          <StaffFaceCard userId={userId} name={person.name} canManage={canEditProfile} />
        )}
      </TabsContent>

      <TabsContent value="employment" className="mt-4 flex flex-col gap-4">
        <EmploymentForm person={person} canManage={canEditProfile} />
        {showPay ? <PayCard userId={userId} /> : null}
      </TabsContent>

      <TabsContent value="role" className="mt-4 flex flex-col gap-4">
        {/* Access is decided with a building: admin or staff there, the roles
              held there and the clinical credential practised there. */}
        <FacilityAccess
          userId={userId}
          personName={person.name}
          assignments={person.facilities}
          canAssign={orgAssigns || assignsSomewhere}
          canAssignAt={canAssignAt}
          isSelf={isSelf}
          isOwner={isOwner}
          canEditLicenses={isSelf || canEditProfile}
        />
      </TabsContent>

      <TabsContent value="licenses" className="mt-4">
        <LicenseCard userId={userId} canEdit={isSelf || canEditProfile} />
      </TabsContent>

      {showHr ? (
        <TabsContent value="hr-docs" className="mt-4 flex flex-col gap-4">
          {/* The same file the HR module shows, not a summary of it: the hiring items they owe,
              every requirement and what is filed against it. A summary here meant opening a second
              screen to do anything about what it said. */}
          <EmployeeHrFile userId={userId} />
        </TabsContent>
      ) : null}

      {hiringOn ? (
        <TabsContent value="hiring" className="mt-4">
          {/* An employee's own hiring file was reachable only by knowing the
              application id. */}
          <HiringHistoryCard userId={userId} />
        </TabsContent>
      ) : null}
    </Tabs>
  );
}
